import { useEffect, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { api, errorText } from "../api";
import { playLocal } from "../audio";
import { meterClass, num } from "../format";
import { useTick } from "../hooks";
import { toast } from "../toast";
import type { Limits, Model, Preset, Quota, Row, Voice, VoiceSettings } from "../types";
import { starRow } from "./Feed";
import { Icons } from "./Icons";
import { ModelPicker } from "./ModelPicker";
import { Popover } from "./Popover";
import { VoicePicker } from "./VoicePicker";
import { VoiceSettingsForm } from "./VoiceSettingsForm";

interface Props {
  text: string;
  setText: (text: string) => void;
  textRef: RefObject<HTMLTextAreaElement>;
  limits: Limits;
  quota: Quota | null;
  locked: boolean;
  admin: boolean;
  cooldownUntil: number;
  lastAudioUrl: string | null;
  sending: boolean;
  onSend: () => void;

  /* What the message will be spoken with. These used to be three cards in a
     300px column that was on screen for the whole stream to be touched twice;
     as chips they are one click away and the composer gets the room. */
  voices: Voice[];
  models: Model[];
  selectedVoice: Voice | null;
  selectedModel: Model | null;
  settings: VoiceSettings;
  modelId: string;
  onVoice: (voice: Voice) => void;
  onModel: (modelId: string) => void;
  onSettings: (settings: VoiceSettings) => void;
  onRefreshVoices: () => void;

  /* The instance's shared characters, in number-key order, already filtered to
     what this user is allowed to use. */
  presets: Preset[];
  activePreset: number | null;
  /* Non-admins may only send as a preset — the voice, model and tuning chips
     are not theirs to touch. */
  presetsOnly: boolean;
  onApplyPreset: (preset: Preset) => void;
  onClearPreset: () => void;
  onSavePreset: () => void;
  onManagePresets: () => void;

  /* Generate first, listen, then decide. Per browser. */
  previewFirst: boolean;
  onPreviewFirst: (on: boolean) => void;
  /* This sender's previews, newest first: generated, not yet on stream. */
  held: Row[];
  /* Bumped on every successful send, so the button can say it worked. */
  sentAt: number;
  /* The tight layout for the mini window: no budget meter, no headings. */
  compact?: boolean;
}

const fail = (error: unknown) => toast(errorText(error), "error");

/* The previews waiting for their sender's decision.

   Each one is a clip that has already cost its credits, so the strip makes the
   two cheap outcomes — send it, or keep it as a pad — as easy to reach as
   throwing it away. It sits directly above the text box, where the eye already
   is after pressing Generate. */
function HeldStrip({ held, admin }: { held: Row[]; admin: boolean }) {
  if (held.length === 0) return null;
  return (
    <div className="held-strip" role="region" aria-label="Previews waiting to be sent">
      {held.map((row) => {
        const ready = Boolean(row.audio_url);
        return (
          <div className={"held" + (ready ? "" : " generating")} key={row.id}>
            <div className="held-text">
              <span className="eyebrow">{ready ? "Preview" : "Generating preview…"}</span>
              <span className="held-msg" title={row.text}>
                {row.text}
              </span>
            </div>
            <div className="held-actions">
              <button
                type="button"
                className="ghost"
                disabled={!ready}
                onClick={() => row.audio_url && playLocal(row.audio_url)}
              >
                {Icons.listen} Listen
              </button>
              {admin && (
                <button
                  type="button"
                  className="ghost"
                  disabled={!ready}
                  title="Save to soundboard"
                  onClick={() => void starRow(row)}
                >
                  {row.starred ? Icons.starred : Icons.star} {row.starred ? "Saved" : "Save"}
                </button>
              )}
              <button
                type="button"
                className="ghost danger"
                onClick={() =>
                  void api(`/api/requests/${row.id}`, { method: "DELETE" }).catch(fail)
                }
              >
                Discard
              </button>
              <button
                type="button"
                className="held-send"
                disabled={!ready}
                onClick={() =>
                  void api(`/api/requests/${row.id}/release`, { method: "POST" })
                    .then(() => toast(`Sent #${row.id} to the stream`, "ok"))
                    .catch(fail)
                }
              >
                {Icons.send} Send to stream
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ⌘ on a Mac, Ctrl everywhere else. The shortcut has always worked; printing
   the right glyph for the machine is what makes it discoverable. */
const SHORTCUT =
  typeof navigator !== "undefined" && /Mac|iP(hone|ad|od)/.test(navigator.platform)
    ? "⌘↵"
    : "Ctrl+↵";

function Chip({
  eyebrow,
  value,
  ariaLabel,
  width,
  children,
}: {
  eyebrow: string;
  value: string;
  ariaLabel: string;
  width?: number;
  children: (close: () => void) => ReactNode;
}) {
  return (
    <Popover
      className="chip"
      ariaLabel={ariaLabel}
      width={width}
      label={
        <>
          <span className="chip-key">{eyebrow}</span>
          <span className="chip-value">{value}</span>
          <span className="chip-caret" aria-hidden="true">
            ▾
          </span>
        </>
      }
    >
      {children}
    </Popover>
  );
}

/** A short readout of the sliders, so the chip says something without opening. */
function tuningLabel(settings: VoiceSettings): string {
  return `sta ${settings.stability.toFixed(2)} · sim ${settings.similarity_boost.toFixed(2)}`;
}

export function Composer({
  text,
  setText,
  textRef,
  limits,
  quota,
  locked,
  admin,
  cooldownUntil,
  lastAudioUrl,
  sending,
  onSend,
  voices,
  models,
  selectedVoice,
  selectedModel,
  settings,
  modelId,
  onVoice,
  onModel,
  onSettings,
  onRefreshVoices,
  presets,
  activePreset,
  presetsOnly,
  onApplyPreset,
  onClearPreset,
  onSavePreset,
  onManagePresets,
  previewFirst,
  onPreviewFirst,
  held,
  sentAt,
  compact = false,
}: Props) {
  useTick(); // the cooldown counts down on its own

  /* A short tick on the button after it worked. The toast says the same, but
     the toast is in a corner and the button is under the cursor. */
  const [justSent, setJustSent] = useState(false);
  useEffect(() => {
    if (!sentAt) return;
    setJustSent(true);
    const timer = window.setTimeout(() => setJustSent(false), 1200);
    return () => window.clearTimeout(timer);
  }, [sentAt]);

  const max = limits.max_chars || 300;
  const cooldownLeft = Math.max(0, cooldownUntil - Date.now());

  /* Whether the composer is free to pick a voice at all. An admin always is —
     the lock is a restriction the streamer puts on other people, and locking
     yourself out of your own instance mid-stream helps nobody. */
  const pinned = presetsOnly && !admin;

  /* One place that decides whether the button works and says why. The button
     used to just go grey: "locked", "muted" and "you typed three characters too
     many" are very different problems and all of them looked identical. */
  const blocked = ((): string | null => {
    if (sending) return "Sending…";
    if (locked && !admin) return "TTS is locked";
    if (quota?.muted) return "You are muted";
    if (pinned && activePreset === null) {
      return presets.length
        ? "Pick a character first"
        : "No characters yet";
    }
    if (text.trim().length === 0) return null; // nothing typed yet: not a problem
    if (text.length > max) return `${text.length - max} over the limit`;
    if (cooldownLeft > 0) return `Wait ${Math.ceil(cooldownLeft / 1000)}s`;
    return null;
  })();

  const disabled = text.trim().length === 0 || blocked !== null;
  const activeName = presets.find((preset) => preset.id === activePreset)?.name;

  const budget = quota?.daily_budget || 0;
  const usedPct = budget ? Math.min(100, ((quota?.used_today ?? 0) / budget) * 100) : 0;

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && !disabled) {
      onSend();
    }
  }

  return (
    <div className={"card composer" + (compact ? " compact" : "")}>
      <h2>
        Say something <span className="spacer"></span>
        {admin && !compact && (
          <>
            <button
              className="link"
              onClick={onSavePreset}
            >
              Save as character
            </button>
            <button className="link" onClick={onManagePresets}>
              Manage
            </button>
          </>
        )}
      </h2>

      {presets.length > 0 ? (
        /* The number is the point: these are meant to be pressed without
           looking, mid-sentence, while the streamer is talking to chat. They
           are the same row for everyone on the instance now, so a mod and the
           streamer press the same key for the same character. */
        <div className="presets" role="group" aria-label="Characters">
          {presets.map((preset, index) => (
            <span
              key={preset.id}
              className={
                "preset" +
                (preset.id === activePreset ? " on" : "") +
                (preset.locked ? " locked" : "")
              }
            >
              <button
                className="preset-apply"
                title={
                  `${preset.voice_name}` + (preset.locked ? " · admins only" : "")
                }
                onClick={() => onApplyPreset(preset)}
              >
                <kbd>{index + 1}</kbd>
                {preset.name}
                {preset.locked && (
                  <span className="preset-mark" aria-label="admins only">
                    ●
                  </span>
                )}
              </button>
            </span>
          ))}
          {/* Only meaningful when there is something to go back to. Under the
              preset lock there is not: free-form is exactly what is refused. */}
          {!pinned && activePreset !== null && (
            <button className="link" onClick={onClearPreset}>
              Clear
            </button>
          )}
        </div>
      ) : (
        pinned && (
          <div className="hint">No characters yet.</div>
        )
      )}

      {pinned ? (
        /* No chips at all: under the lock the preset *is* the voice, the model
           and the tuning, and a form whose values the server would ignore is
           worse than no form. */
        <div className="hint">
          {activeName ? `Speaking as ${activeName}` : "Pick a character"}
        </div>
      ) : (
      <div className="chips">
        <Chip
          eyebrow="voice"
          value={selectedVoice?.name ?? "none"}
          ariaLabel="Choose a voice"
          width={330}
        >
          {(close) => (
            <VoicePicker
              voices={voices}
              selected={selectedVoice}
              onSelect={(voice) => {
                onVoice(voice);
                close();
              }}
              onRefresh={onRefreshVoices}
            />
          )}
        </Chip>

        <Chip
          eyebrow="model"
          value={selectedModel?.name || modelId || "none"}
          ariaLabel="Choose a model"
          width={330}
        >
          {() => <ModelPicker models={models} modelId={modelId} onChange={onModel} />}
        </Chip>

        <Chip
          eyebrow="tuning"
          value={tuningLabel(settings)}
          ariaLabel="Voice settings"
          width={330}
        >
          {() => (
            <VoiceSettingsForm
              modelId={modelId}
              settings={settings}
              onChange={onSettings}
            />
          )}
        </Chip>
      </div>
      )}

      <HeldStrip held={held} admin={admin} />

      <textarea
        id="text"
        aria-label="Message"
        placeholder="Type here…"
        maxLength={4000}
        ref={textRef}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <div className="composer-bar">
        <span className={"count" + (text.length > max ? " over" : "")}>
          {text.length} / {max}
        </span>
        {blocked && <span className="blocked-why">{blocked}</span>}
        <span className="spacer"></span>
        <label className="check preview-toggle">
          <input
            type="checkbox"
            checked={previewFirst}
            onChange={(event) => onPreviewFirst(event.target.checked)}
          />
          Preview first
        </label>
        {!compact && (
        <button
          className="ghost"
          onClick={() => {
            if (!lastAudioUrl) {
              toast("Nothing generated yet");
              return;
            }
            playLocal(lastAudioUrl);
          }}
        >
          Last clip
        </button>
        )}
        <button
          id="send"
          className={(sending ? "busy" : "") + (justSent ? " done" : "")}
          disabled={disabled}
          aria-busy={sending}
          onClick={onSend}
        >
          <span className="send-state" aria-hidden="true">
            {sending ? <span className="spinner" /> : justSent ? "✓" : null}
          </span>
          {previewFirst ? "Preview" : "Send"} <kbd>{SHORTCUT}</kbd>
        </button>
      </div>
      {!compact && (
        <>
          <div className={meterClass(usedPct, 70, 90)}>
            <div style={{ width: `${usedPct}%` }} />
          </div>
          <div className="muted" id="budget-note">
            {budget
              ? `${num(quota?.used_today)} / ${num(budget)} characters today`
              : ""}
          </div>
        </>
      )}
    </div>
  );
}
